import { useMemo, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  BellRing,
  Briefcase,
  CheckCircle2,
  CircleHelp,
  ExternalLink,
  Eye,
  Globe,
  Layers3,
  LifeBuoy,
  Lock,
  Shield,
  Users,
} from "lucide-react";
import { Helmet } from "@/components/Helmet";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  CampaignFaq,
  CtaSection,
  IconCardGrid,
  PricingCards,
  SectionShell,
  StepList,
  TerminalNote,
  TrialExplainerStrip,
} from "@/components/campaign/CampaignSections";
import {
  CAMPAIGN_SIGNUP_HREF,
  CAMPAIGN_SUPPORT_HREF,
  CampaignLayout,
  CampaignSupportLink,
} from "@/components/campaign/CampaignLayout";
import {
  captureCampaignEvent,
  useCampaignPageView,
  useCampaignSectionView,
} from "@/lib/campaignAnalytics";

const CANONICAL_URL = "https://shadowscan.com.au/linkedin";
const HELP_URL = "/help/add-domains";
const EVALUATION_LABEL = "Start 7-day evaluation";

const LinkedInLanding = () => {
  const location = useLocation();
  const pricingRef = useRef<HTMLElement | null>(null);
  const currentPath = `${location.pathname}${location.hash || ""}`;

  useCampaignPageView("linkedin_landing_viewed", {
    page: "linkedin_landing",
    path: location.pathname,
  });
  useCampaignSectionView(pricingRef, "linkedin_pricing_viewed", {
    page: "linkedin_landing",
    path: location.pathname,
    location: "pricing_section",
  });

  const handleCtaClick = (ctaLocation: string, target: string, isPrimary = false) => {
    captureCampaignEvent("linkedin_cta_clicked", {
      page: "linkedin_landing",
      location: ctaLocation,
      target,
    });

    if (isPrimary) {
      captureCampaignEvent("linkedin_trial_started", {
        page: "linkedin_landing",
        location: ctaLocation,
        target,
      });
    }
  };

  const handleSupportClick = (locationLabel: string) => {
    captureCampaignEvent("linkedin_support_clicked", {
      page: "linkedin_landing",
      location: locationLabel,
      target: "support_email",
    });
  };

  const faqItems = useMemo(
    () => [
      {
        question: "What does ShadowScan cost per domain?",
        answer: (
          <p>
            Essential is AUD $50 per domain per month and Premium is AUD $185 per domain
            per month. Both plans are shown in the pricing section, and larger portfolios
            can discuss bulk pricing tiers.
          </p>
        ),
      },
      {
        question: "What happens when I start the evaluation path?",
        answer: (
          <p>
            You create your portal account first, then add one to three domains and
            complete onboarding. In the current live flow, standard billing applies when
            monitoring is activated for those domains.
          </p>
        ),
      },
      {
        question: "When does billing begin?",
        answer: (
          <p>
            In the current live flow, standard billing setup applies when you add domains
            and activate monitoring. The page is designed to help you evaluate onboarding,
            workflow fit, and pricing before scaling further.
          </p>
        ),
      },
      {
        question: "Do I need a credit card to start the evaluation?",
        answer: (
          <p>
            You can create a ShadowScan portal account without entering a payment method.
            In the current live flow, when you add domains and activate monitoring, standard
            billing setup applies. This page is designed to help you evaluate the fit, the
            onboarding path, and the operational workflow before expanding further.
          </p>
        ),
      },
      {
        question: "How many domains should I evaluate first?",
        answer: (
          <p>
            Start with a small working set, usually one to three WordPress sites. That is
            enough to assess portfolio visibility, onboarding effort, alert context, and
            pricing fit without creating unnecessary support overhead.
          </p>
        ),
      },
      {
        question: "Will ShadowScan replace my existing WAF or edge protection?",
        answer: (
          <p>
            No. ShadowScan focuses on continuous monitoring, application-level protections,
            and human-led response coordination. If you already use a WAF or edge controls,
            ShadowScan is intended to work alongside them rather than replace them.
          </p>
        ),
      },
      {
        question: "What happens if onboarding fails?",
        answer: (
          <p>
            You can use the onboarding help page for the most common setup issues, including
            domain mismatch, custom <code>wp-admin</code> paths, expired pairing codes, and
            host restrictions. If you still get stuck, email{" "}
            <a
              href={CAMPAIGN_SUPPORT_HREF}
              onClick={() => handleSupportClick("faq")}
              className="text-primary underline underline-offset-4"
            >
              support@shadowscan.com.au
            </a>{" "}
            and include the domain name plus a short note on where the setup stopped.
          </p>
        ),
      },
      {
        question: "What happens after the first week of evaluation?",
        answer: (
          <p>
            You should have enough signal to decide whether the onboarding path, monitoring
            visibility, and per-domain pricing fit your workflow. From there, you can keep
            rolling out domains through the standard billing flow, or pause and review with
            ShadowScan support before adding more sites.
          </p>
        ),
      },
      {
        question: "Is this for agencies, developers, or individual site owners?",
        answer: (
          <p>
            This campaign page is tuned for agencies, freelancers, and technical operators
            managing multiple WordPress sites. Individual technical site owners can also use
            it, especially if they want a managed monitoring layer without building their own
            internal workflow.
          </p>
        ),
      },
    ],
    [],
  );

  const stickyCta = (
    <>
      <Button
        asChild
        className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90"
      >
        <Link
          to={CAMPAIGN_SIGNUP_HREF}
          onClick={() => handleCtaClick("mobile_sticky", CAMPAIGN_SIGNUP_HREF, true)}
        >
          {EVALUATION_LABEL}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </Button>
      <Button asChild variant="outline" className="min-h-11 shrink-0">
        <Link
          to={HELP_URL}
          onClick={() => handleCtaClick("mobile_sticky", HELP_URL)}
        >
          Onboarding help
        </Link>
      </Button>
    </>
  );

  return (
    <CampaignLayout
      pricingHref={currentPath.includes("#pricing") ? "#pricing" : "/linkedin#pricing"}
      helpHref={HELP_URL}
      currentPath="landing"
      stickyCta={stickyCta}
      headerCtaLabel="Start evaluation"
      headerCtaHref={CAMPAIGN_SIGNUP_HREF}
    >
      <Helmet>
        <title>ShadowScan 7-Day WordPress Security Evaluation</title>
        <meta
          name="description"
          content="Managed WordPress security for teams running multiple sites. Start a 7-day ShadowScan evaluation, add a few domains, and assess monitoring, protections, and human-led response."
        />
        <meta property="og:title" content="ShadowScan 7-Day WordPress Security Evaluation" />
        <meta
          property="og:description"
          content="Managed WordPress security for agencies, freelancers, and technical operators managing multiple WordPress sites. Review pricing, start an account, and evaluate onboarding and workflow fit."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={CANONICAL_URL} />
        <meta property="og:image" content="https://shadowscan.com.au/og-image.png" />
        <meta
          name="twitter:title"
          content="ShadowScan 7-Day WordPress Security Evaluation"
        />
        <meta
          name="twitter:description"
          content="Continuous monitoring, application-level protections, and human oversight for agencies, freelancers, and technical teams managing multiple WordPress sites."
        />
        <meta name="twitter:image" content="https://shadowscan.com.au/twitter-image.png" />
        <meta
          name="linkedin:description"
          content="Managed WordPress security for agencies, developers, and technical site owners managing multiple sites."
        />
      </Helmet>

      <section className="relative overflow-hidden py-16 md:py-24">
        <div className="container px-4">
          <div className="grid gap-10 lg:grid-cols-[1.15fr,0.85fr] lg:items-center">
            <div className="space-y-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-2 font-mono text-xs uppercase tracking-[0.22em] text-primary">
                <span aria-hidden="true" className="h-2 w-2 rounded-full bg-[#22C373]" />
                Built for multi-site WordPress operators
              </div>

              <div className="space-y-5">
                <p className="max-w-3xl text-sm uppercase tracking-[0.18em] text-muted-foreground">
                  For agencies, freelancers, and technical operators managing client or
                  internal WordPress portfolios.
                </p>
                <h1 className="max-w-4xl text-4xl font-bold leading-tight md:text-5xl lg:text-6xl">
                  Managed WordPress security for teams running multiple sites
                </h1>
                <p className="max-w-3xl text-lg leading-relaxed text-muted-foreground md:text-xl">
                  Continuous monitoring, application-level protections, and human oversight
                  for WordPress sites you manage across client portfolios, internal estates,
                  or mixed agency workloads.
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                {[
                  "Predictable per-domain pricing",
                  "Human support if onboarding stalls",
                  "Works alongside existing WAF or edge controls",
                  "Australian managed service",
                ].map((item) => (
                  <span
                    key={item}
                    className="rounded-full border border-border/80 bg-card/80 px-4 py-2 text-sm text-muted-foreground"
                  >
                    {item}
                  </span>
                ))}
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                <Button
                  asChild
                  size="lg"
                  className="bg-accent text-accent-foreground hover:bg-accent/90"
                >
                  <Link
                    to={CAMPAIGN_SIGNUP_HREF}
                    onClick={() => handleCtaClick("hero_primary", CAMPAIGN_SIGNUP_HREF, true)}
                  >
                    {EVALUATION_LABEL}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <a
                    href="#pricing"
                    onClick={() => handleCtaClick("hero_secondary", "#pricing")}
                  >
                    See pricing
                  </a>
                </Button>
              </div>
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Need to see the onboarding steps first?{" "}
                  <Link
                    to={HELP_URL}
                    onClick={() => handleCtaClick("hero_help_link", HELP_URL)}
                    className="text-primary underline underline-offset-4"
                  >
                    Review onboarding help
                  </Link>
                  .
                </p>
                <p className="text-sm text-muted-foreground">
                  Start with 1-3 sites to evaluate workflow fit before expanding further.
                </p>
              </div>

              <div className="rounded-2xl border border-border/80 bg-card/80 p-4">
                <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-accent">What happens when you start</p>
                <ul className="mt-3 space-y-3 text-sm text-muted-foreground">
                  {[
                    "Create your portal account.",
                    "Add 1-3 domains and complete onboarding.",
                    "Standard billing applies when monitoring is activated.",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-[#22C373]" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="space-y-5">
              <TerminalNote
                label="operator fit"
                title="Designed for developers, agencies, and technical site owners"
                body="ShadowScan is a calm managed layer for portfolios where too many dashboards, too much alert noise, and unclear response ownership create operational drag. The service includes human review on important alerts and an Australian support path when you need help getting setup back on track."
              />
              <Card className="border-border/80 bg-card/80">
                <CardContent className="space-y-4 p-6">
                  <div className="flex items-center gap-3">
                    <Shield className="h-5 w-5 text-primary" />
                    <span className="font-medium">What you can evaluate in your first week</span>
                  </div>
                  <ul className="space-y-3 text-sm text-muted-foreground">
                    {[
                      "Portal account setup and initial domain onboarding",
                      "Monitoring visibility across a small working set of sites",
                      "Alert context and application-level protection posture",
                      "Whether Essential or Premium fits the way you manage sites",
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-[#22C373]" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <SectionShell
        eyebrow="Why this page exists"
        title="A focused path for people managing more than one WordPress site"
        description="The campaign is tuned for portfolio-style buyers who want visibility, clear pricing, and a low-drama way to evaluate a managed security layer without sending LinkedIn traffic through a generic homepage."
      >
        <IconCardGrid
          columns="four"
          items={[
            {
              icon: Layers3,
              title: "Too many dashboards",
              description:
                "Multiple sites often means fragmented visibility and extra time spent confirming what actually matters.",
            },
            {
              icon: BellRing,
              title: "Noisy alerting",
              description:
                "Operators need signal quality and context, not constant interruption with weak prioritization.",
            },
            {
              icon: AlertTriangle,
              title: "Unclear incident ownership",
              description:
                "When something goes wrong, agencies and freelancers still need a documented response path.",
            },
            {
              icon: Briefcase,
              title: "Client risk without a security team",
              description:
                "ShadowScan helps teams reduce risk without pretending every operator has in-house security coverage.",
            },
          ]}
        />
      </SectionShell>

      <SectionShell
        eyebrow="How it works"
        title="Start with a small rollout and inspect the operational fit"
        description="Use the first week to see whether ShadowScan fits the way you actually manage WordPress sites day to day."
      >
        <StepList
          items={[
            {
              step: "Step 1",
              title: "Start the evaluation path",
              description:
                "Create your portal account and begin with Essential intent so the evaluation stays simple and predictable.",
            },
            {
              step: "Step 2",
              title: "Add a few domains",
              description:
                "Onboard one to three WordPress sites, install the connector, and complete the pairing steps inside the portal.",
            },
            {
              step: "Step 3",
              title: "Review signals and workflow fit",
              description:
                "Assess monitoring visibility, alert context, application-level protections, and whether the pricing matches your portfolio needs.",
            },
          ]}
        />
      </SectionShell>

      <SectionShell
        eyebrow="What to evaluate"
        title="Use the first week to test the practical pieces"
        description="This campaign page keeps the evaluation grounded in the parts you can actually verify with the live product flow."
      >
        <IconCardGrid
          columns="four"
          items={[
            {
              icon: Globe,
              title: "Portal and domain onboarding",
              description:
                "Confirm the add-domain path, plugin install process, and pairing steps on live WordPress sites.",
            },
            {
              icon: Eye,
              title: "Monitoring visibility",
              description:
                "Review how ShadowScan surfaces suspicious behavior and portfolio context across the sites you manage.",
            },
            {
              icon: Lock,
              title: "Application-level protections",
              description:
                "Inspect the protection posture without assuming it replaces external WAF or edge controls.",
            },
            {
              icon: Users,
              title: "Human oversight and fit",
              description:
                "Decide whether the operating model, alert style, and support path match your workflow and client expectations.",
            },
          ]}
        />
      </SectionShell>

      <SectionShell
        eyebrow="First-week rollout"
        title="Evaluate the current live path without overcommitting"
        description="The goal is a low-friction evaluation, but also an honest one."
      >
        <TrialExplainerStrip
          title="Create your account now, then use the first week to assess onboarding and workflow fit."
          body="For this campaign, ShadowScan encourages a small practical rollout: create a portal account, add a few WordPress domains, verify the connector path, and review the monitoring and protection posture with your own sites."
          note="You can create your account first. When you add domains and activate monitoring, the current live flow uses the standard billing setup."
        />
      </SectionShell>

      <SectionShell
        id="pricing"
        eyebrow="Pricing"
        title="Predictable pricing for each domain you manage"
        description="Start with Essential for baseline managed monitoring and guided remediation. Move to Premium when you need analyst-led monitoring, incident ownership, and remediation within scope."
        className="scroll-mt-24"
      >
        <section ref={pricingRef}>
          <PricingCards
            items={[
              {
                name: "Essential",
                price: "AUD $50",
                summary:
                  "Baseline managed monitoring, application-level protections, and guided remediation for WordPress portfolios that need a calm, repeatable operating model.",
                badge: "Best starting point",
                features: [
                  "Continuous monitoring with validated alert context",
                  "Application-level protections that reduce common attack paths",
                  "Guided remediation steps and workflow visibility",
                  "Useful starting point for agencies and multi-site operators",
                  "Recommended for a first rollout and initial evaluation",
                ],
              },
              {
                name: "Premium",
                price: "AUD $185",
                summary:
                  "Analyst-led monitoring, incident ownership, and remediation within scope for teams that want ShadowScan to take a deeper operational role.",
                features: [
                  "Everything in Essential",
                  "Analyst-led monitoring and higher-touch incident coordination",
                  "Incident ownership and remediation within agreed scope",
                  "Better fit when response responsibility needs to be explicit",
                  "Best when you want higher-touch response ownership",
                ],
              },
            ]}
          />
          <p className="mt-5 text-sm text-muted-foreground">
            Month-to-month pricing, charged per active domain. In the current live flow,
            standard billing applies when domains are activated. Bulk pricing tiers are
            available for larger portfolios.
          </p>
        </section>
      </SectionShell>

      <SectionShell
        eyebrow="Start with a small rollout"
        title="Move from pricing review into a real account and practical onboarding"
      >
        <CtaSection
          eyebrow="Primary CTA"
          title="Start the 7-day evaluation path"
          description="Go directly into the existing portal signup flow with Essential intent and LinkedIn campaign source tracking."
          primaryLabel={EVALUATION_LABEL}
          primaryHref={CAMPAIGN_SIGNUP_HREF}
          secondaryLabel="How evaluation onboarding works"
          secondaryHref={HELP_URL}
          onPrimaryClick={() => handleCtaClick("midpage_cta", CAMPAIGN_SIGNUP_HREF, true)}
          onSecondaryClick={() => handleCtaClick("midpage_cta", HELP_URL)}
        />
      </SectionShell>

      <SectionShell
        eyebrow="Need help?"
        title="If domain onboarding stalls, there is a clear next step"
        description="Use the onboarding guide first for the common blockers, then escalate to support with enough context for a useful reply."
      >
        <div className="grid gap-5 lg:grid-cols-[1fr,0.8fr]">
          <Card className="border-border/80 bg-card/80">
            <CardContent className="space-y-4 p-6">
              <div className="flex items-center gap-3">
                <CircleHelp className="h-5 w-5 text-primary" />
                <h3 className="text-xl font-semibold">Need help adding domains?</h3>
              </div>
              <p className="text-sm leading-relaxed text-muted-foreground">
                The help page covers the real live onboarding path: add the domain, install
                the connector, generate a pairing code, handle custom <code>wp-admin</code>
                paths, and resolve common setup blockers.
              </p>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Button asChild variant="outline">
                  <Link
                    to={HELP_URL}
                    onClick={() => handleCtaClick("help_block", HELP_URL)}
                  >
                    Open onboarding help
                  </Link>
                </Button>
                <a
                  href={CAMPAIGN_SUPPORT_HREF}
                  onClick={() => handleSupportClick("help_block")}
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80"
                >
                  Contact support
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/80 bg-card/80">
            <CardContent className="space-y-4 p-6">
              <div className="flex items-center gap-3">
                <LifeBuoy className="h-5 w-5 text-accent" />
                <h3 className="text-xl font-semibold">What to include if you email support</h3>
              </div>
              <ul className="space-y-3 text-sm text-muted-foreground">
                {[
                  "The domain name you were onboarding",
                  "Whether the issue happened in the portal, plugin install, or pairing step",
                  "If the site uses a custom wp-admin path or host restrictions",
                  "Any error text or screenshot that helps narrow the failure",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <BadgeCheck className="mt-0.5 h-4 w-4 flex-none text-[#22C373]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <CampaignSupportLink className="pt-1" />
            </CardContent>
          </Card>
        </div>
      </SectionShell>

      <SectionShell
        eyebrow="FAQ"
        title="Answer the main objections before they slow the rollout"
        description="Keep the evaluation path honest, practical, and clear."
      >
        <CampaignFaq items={faqItems} />
      </SectionShell>

      <SectionShell
        eyebrow="Ready to Give it Go?"
        title="See whether ShadowScan fits the way you manage WordPress portfolios"
      >
        <CtaSection
          eyebrow="Next step"
          title="Create your account, add a few sites, and review the workflow"
          description="The fastest way to evaluate fit is to run the real onboarding path with a small number of sites you already manage."
          primaryLabel={EVALUATION_LABEL}
          primaryHref={CAMPAIGN_SIGNUP_HREF}
          secondaryLabel="See onboarding help"
          secondaryHref={HELP_URL}
          onPrimaryClick={() => handleCtaClick("final_cta", CAMPAIGN_SIGNUP_HREF, true)}
          onSecondaryClick={() => handleCtaClick("final_cta", HELP_URL)}
        />
      </SectionShell>
    </CampaignLayout>
  );
};

export default LinkedInLanding;
